import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { testId } from '../testId';
import styles from './LiveRegion.module.css';

// The ONE aria-live region of the app (ARCH §9.5, REQ-281): it announces staged / published / error and
// whatever else must reach a screen reader without moving focus. It lives outside the router so it
// survives navigation. Components call `useAnnounce()`; nothing renders a second live region.

export type Politeness = 'polite' | 'assertive';
interface Announcement {
  text: string;
  politeness: Politeness;
  /** Increments per announcement so the same sentence twice is still spoken twice. */
  seq: number;
}

const listeners = new Set<() => void>();
let current: Announcement | null = null;
let seq = 0;

export function announce(text: string, politeness: Politeness = 'polite'): void {
  current = { text, politeness, seq: ++seq };
  for (const listener of [...listeners]) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
const getSnapshot = () => current;

/** For tests: forget the last announcement. */
export function resetAnnouncements(): void {
  current = null;
}

export function useAnnounce(): typeof announce {
  return useCallback(announce, []);
}

/** Two regions (polite + assertive) so an error can interrupt while a routine notice waits its turn. */
export function LiveRegion() {
  const announcement = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  // A live region only speaks when its content CHANGES: clear then set, so repeats are announced too.
  const [shown, setShown] = useState<Announcement | null>(null);
  useEffect(() => {
    if (!announcement) return;
    setShown(null);
    const id = setTimeout(() => setShown(announcement), 50);
    return () => clearTimeout(id);
  }, [announcement]);

  return (
    <>
      <div
        className={styles.region}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid={testId('app', 'live-region')}
      >
        {shown?.politeness === 'polite' ? shown.text : ''}
      </div>
      <div
        className={styles.region}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        data-testid={testId('app', 'live-region', 'assertive')}
      >
        {shown?.politeness === 'assertive' ? shown.text : ''}
      </div>
    </>
  );
}
